import dayjs from "dayjs";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { sendFbEvent } from "../common/api/helpers/apiFunctions/user";
import { useIsFirstSearchValue, useRoomDataValue } from "../common/data/call";
import { useUser } from "../common/hooks/useUser";
import { getFromLocalStorage, LocalStorageKeys } from "../common/utils/localStorage";
import { localRoutes } from "../config/localRoutes";
import { AuthStatus, RegistrationStatus } from "../types";
import { FbPixel, getFbParams } from "./facebookPixel";
import { resetRoomStatistic, Analytics } from ".";

export const useSendingVisitEventsByLocation = () => {
  const { userDetails, personalDetails, userAuthStatus, userCompletedRegistration } = useUser();
  const roomData = useRoomDataValue();
  const { pathname } = useLocation();
  const lastPathname = useRef("");
  const isFirstSearch = useIsFirstSearchValue();
  const flags = useFlags();
  const client = useLDClient();

  useEffect(() => {
    const isLoading =
      userAuthStatus === AuthStatus.LOADING || userCompletedRegistration === RegistrationStatus.LOADING || !client;

    if (pathname === lastPathname.current || isLoading) return;

    lastPathname.current = pathname;

    switch (pathname) {
      case localRoutes.homeSpecify: {
        const data = {
          "Screen Name": "Welcome Screen V1",
        };

        Analytics.track("Welcome Screen Visit", { ...data });
        FbPixel?.track("ViewContent", { ...data });

        break;
      }

      case localRoutes.login: {
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get("otp");
        const number = searchParams.get("phone_number");
        const anonymousId = searchParams.get("anonymous_id");

        if (code && number) {
          if (anonymousId) {
            Analytics.alias(anonymousId);
          }

          Analytics.track("Verification Link Sent Screen Visit");
        }

        Analytics.track("Login Screen Visit", {
          "Screen Name": "Login Phone Enter V1",
        });

        break;
      }

      case localRoutes.register: {
        const data = {
          "Screen Name": "Registration Phone Submit V1",
        } as { [key: string]: number | string };

        if (flags.registrationScreenFg && flags.registrationScreenFg > 0) {
          data.Version = flags.registrationScreenFg;
        }

        Analytics.track("Registration Phone Submit Visit", { ...data });

        break;
      }

      case localRoutes.accountSetupPages.basic: {
        Analytics.track("Registration Name and DOB Screen Visit", {
          "Screen Name": "Registration Name and DOB V1",
        });

        break;
      }

      case localRoutes.accountSetupPages.gender: {
        Analytics.track("Registration Gender Screen Visit", {
          "Screen Name": "Registration Gender V1",
        });

        break;
      }

      case localRoutes.accountSetupPages.state: {
        Analytics.track("Registration Location Screen Visit", {
          "Screen Name": "Registration Location V1",
        });

        break;
      }

      case localRoutes.accountSetupPages.education: {
        Analytics.track("Registration Education Screen Visit", {
          "Screen Name": "Registration Education V1",
        });

        break;
      }

      case localRoutes.betaFlow.welcome: {
        Analytics.setSuperPropertiesOnce({
          "Joined Waiting List Date": dayjs().utc(true).toISOString(),
        });
        Analytics.setUserPropertiesOnce({
          "Joined Waiting List Date": dayjs().utc(true).toISOString(),
        });
        Analytics.track("Waiting List Visit");
        if (FbPixel) {
          FbPixel?.track("Schedule", {
            ...getFbParams(userDetails, personalDetails?.phoneNumber),
          });
          sendFbEvent("SCHEDULE", Number(userDetails.userId));
        }

        break;
      }

      case localRoutes.betaFlow.finish: {
        Analytics.track("Waiting List Invite Friends Visit");

        break;
      }

      case localRoutes.betaFlow.success: {
        Analytics.track("Waiting List Thank you for sharing Visit");

        break;
      }

      case localRoutes.accountSetupPages.email: {
        Analytics.track("Email Visit");

        break;
      }

      case localRoutes.accountSetupPages.bio: {
        Analytics.track("Bio Visit");

        break;
      }

      case localRoutes.accountSetupPages.avatar: {
        Analytics.track("Profile Pic Visit");

        break;
      }

      case localRoutes.accountSetupPages.interests: {
        Analytics.track("Interests Visit");

        break;
      }

      case localRoutes.onboardingPermissions: {
        Analytics.track("OnBoarding Permissions Visit");

        break;
      }

      case localRoutes.getChannel: {
        const linkSentBy = getFromLocalStorage(LocalStorageKeys.invitationSentBy);

        const data = {
          "Screen Name": "Find Available Room",
        } as {
          [key: string]: string;
        };

        if (linkSentBy) {
          data["Sent By"] = linkSentBy;
        }

        if (isFirstSearch && !linkSentBy) {
          Analytics.track("First Room Counter Screen Visit", data);
        } else {
          Analytics.track("Find Room Counter Screen Visit", data);
        }

        break;
      }

      case localRoutes.videoCall: {
        const { userId, fullname } = userDetails;

        const firstRoomVisitDate = Analytics.getSuperProperty("First Room Visit Date");
        if (userId) {
          if (!firstRoomVisitDate) {
            Analytics.setSuperPropertiesOnce({
              "First Room Visit Date": dayjs().utc(true).toISOString(),
            });
            Analytics.track("First Room Visit", {
              "User Name": fullname,
              "Room Participants": (roomData?.numberOfParticipants || 0) + 1,
            });
          } else {
            Analytics.incrementSuperProperties({
              "Rooms Visited Lifetime": 1,
            });
          }
        }

        resetRoomStatistic();

        break;
      }

      case localRoutes.findAvailableChannel: {
        Analytics.track("Out of Room Visit", {
          "Screen Name": "Out of Room V1",
        });

        break;
      }

      case localRoutes.notFoundChannel: {
        Analytics.track("No Room Found Screen Visit", {
          "Screen Name": "No Room Found Screen",
        });

        break;
      }

      case localRoutes.blockedPermission: {
        Analytics.track("No Permission Page Visit");

        break;
      }

      case localRoutes.notAvailableTime: {
        Analytics.track("User Slots Page Visit");

        break;
      }

      default:
        break;
    }
  }, [
    pathname,
    userDetails,
    personalDetails,
    lastPathname,
    roomData,
    userAuthStatus,
    userCompletedRegistration,
    isFirstSearch,
    flags,
    client,
  ]);
};
